/**
 * Root console: System monitoring. Reachable by the root user only, so its UI text is
 * hardcoded English (not i18n) by design. Do not introduce translation keys here.
 */
import React, { useState, useEffect } from "react";
import { CustomButton } from "../components/atoms/CustomButton";
import { Helmet } from "react-helmet-async";
import { Spinner } from "../components/atoms/Spinner";
import { AlertMessage } from "../components/atoms/AlertMessage";
import { HiRefresh } from "react-icons/hi";
import moment from "moment";
import useAuth from "../hooks/useAuth";
import HttpService from "../services/http";
import PaymentModuleService, {
  PaymodServerMonitoringData,
} from "../services/paymentModule";
import { toast } from "react-toastify";
import { StatusBadge, BadgeTone } from "../components/atoms/StatusBadge";

interface ServerUptime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  formatted: string;
}

interface MemoryInfo {
  rss_mb: number;
  heap_total_mb: number;
  heap_used_mb: number;
  heap_available_mb: number;
  heap_used_percentage: number;
  heap_available_percentage: number;
  external_mb: number;
  array_buffers_mb: number;
  heap_size_limit_mb?: number;
  heap_used_vs_limit_percentage?: number;
  container_memory_limit_mb?: number | null;
  memory_used_vs_container_limit_percentage?: number | null;
}

interface CpuInfo {
  usage_percentage: number;
  cores: number;
  model: string;
  speed_mhz: number;
  process_cpu_usage: {
    user: number;
    system: number;
  };
}

interface DatabaseInfo {
  status: string;
  response_time_ms: number;
  connection_pool: {
    pool_size: number;
    pool_min: number;
    pool_acquire: number;
  };
}

interface CacheInfo {
  redis_active: boolean;
  redis_status: string;
  redis_response_time_ms: number;
  redis_key_count: number;
  redis_host: string;
  node_cache_key_count: number;
  node_cache_hits?: number;
  node_cache_misses?: number;
  node_cache_details?: {
    meta_key_cache: boolean;
    event_dedup_entries: number;
  };
}

interface EnvironmentInfo {
  node_env: string;
  logger_level: string;
  apm_active: boolean;
  cron_active: string;
}

interface SystemMonitoringData {
  server_version: string;
  server_uptime_seconds: number;
  server_uptime: ServerUptime;
  server_status: string;
  timestamp: string;
  memory: MemoryInfo;
  cpu: CpuInfo;
  database: DatabaseInfo;
  cache: CacheInfo;
  environment: EnvironmentInfo;
}

type HealthTone = "ok" | "warn" | "down" | "neutral";

const HEALTH_TONES: Record<HealthTone, BadgeTone> = {
  ok: "success",
  warn: "warning",
  down: "failure",
  neutral: "gray",
};

const HealthPill: React.FC<{
  tone: HealthTone;
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <StatusBadge color={HEALTH_TONES[tone]}>{children}</StatusBadge>
);

const Metric: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  valueClassName?: string;
}> = ({ label, value, hint, valueClassName }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3">
    <p className="mb-1 text-xs text-gray-500">{label}</p>
    <p
      className={`text-base font-semibold tabular-nums ${
        valueClassName || "text-gray-900"
      }`}
    >
      {value}
    </p>
    {hint !== undefined && (
      <p className="text-xs tabular-nums text-gray-500">{hint}</p>
    )}
  </div>
);

const ServicePanel: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>
    {children}
  </div>
);

const System: React.FC = () => {
  const { customer_identification, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SystemMonitoringData | null>(null);

  const [paymodLoading, setPaymodLoading] = useState(true);
  const [paymodRefreshing, setPaymodRefreshing] = useState(false);
  const [paymodError, setPaymodError] = useState<string | null>(null);
  const [paymodData, setPaymodData] =
    useState<PaymodServerMonitoringData | null>(null);

  const [aiEngineLoading, setAiEngineLoading] = useState(true);
  const [aiEngineError, setAiEngineError] = useState<string | null>(null);
  const [aiEngineData, setAiEngineData] = useState<any | null>(null);

  const [sumLoading, setSumLoading] = useState(true);
  const [sumData, setSumData] = useState<any | null>(null);

  const fetchSystemMonitoring = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await HttpService.get(
        `/${customer_identification}/${user.entity_identification}/system/monitoring`
      );

      setData(response?.data?.element);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch UMD monitoring data";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchPaymodMonitoring = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setPaymodRefreshing(true);
      } else {
        setPaymodLoading(true);
      }
      setPaymodError(null);

      const response = await PaymentModuleService.getServerMonitoring(
        customer_identification,
        user.entity_identification
      );

      setPaymodData(response?.data?.element || null);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch Paymod monitoring data";
      setPaymodError(errorMessage);
      if (isRefresh) {
        toast.error(errorMessage);
      }
    } finally {
      setPaymodLoading(false);
      setPaymodRefreshing(false);
    }
  };

  const fetchAiEngineMonitoring = async () => {
    try {
      setAiEngineLoading(true);
      setAiEngineError(null);
      const response = await HttpService.get(
        `/${customer_identification}/${user.entity_identification}/system/ai-engine/health`
      );
      setAiEngineData(response?.data?.element || null);
    } catch (err: any) {
      setAiEngineError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch AI engine monitoring data"
      );
    } finally {
      setAiEngineLoading(false);
    }
  };

  const fetchSelfMonitoring = async () => {
    try {
      setSumLoading(true);
      const response = await HttpService.get(
        `/${customer_identification}/${user.entity_identification}/system/self-monitoring`
      );
      setSumData(response?.data?.element || null);
    } catch {
      setSumData(null);
    } finally {
      setSumLoading(false);
    }
  };

  const fetchAllMonitoring = async (isRefresh = false) => {
    await Promise.all([
      fetchSystemMonitoring(isRefresh),
      fetchPaymodMonitoring(isRefresh),
      fetchAiEngineMonitoring(),
      fetchSelfMonitoring(),
    ]);
  };

  useEffect(() => {
    fetchAllMonitoring();
    const interval = setInterval(() => {
      fetchAllMonitoring(true);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusPill = (status: string) => {
    const isUp = status === "ok" || status === "UP" || status === "running";
    return (
      <HealthPill tone={isUp ? "ok" : "down"}>
        {isUp ? "Online" : "Offline"}
      </HealthPill>
    );
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 70) return "text-amber-600";
    return "text-green-600";
  };

  const formatTimestamp = (value?: string | null) =>
    value ? moment(value).format("lll") : "—";

  const noData = <p className="text-sm text-gray-500">No data available</p>;

  const panelSpinner = (
    <div className="flex justify-center py-4">
      <Spinner size="md" />
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex h-64 items-center justify-center">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="container mx-auto py-6">
        <AlertMessage color="failure">
          <span className="font-medium">Error!</span> {error}
        </AlertMessage>
      </div>
    );
  }

  const isRefreshing = refreshing || paymodRefreshing;

  return (
    <div className="system-monitoring-page">
      <Helmet>
        <title>System monitoring | Super User Manager</title>
        <meta
          name="description"
          content="System monitoring dashboard for the UMD service"
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              System monitoring
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Health of the UMD, Paymod, AI engine and SUM services
            </p>
          </div>
          <CustomButton
            variant="secondary"
            size="sm"
            onClick={() => fetchAllMonitoring(true)}
            disabled={isRefreshing}
            comp={
              <span className="flex items-center gap-2">
                <HiRefresh
                  className={isRefreshing ? "animate-spin" : ""}
                  size={16}
                />
                {isRefreshing ? "Refreshing..." : "Refresh all"}
              </span>
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-6">
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              Server status overview
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <ServicePanel title="SUM server">
                {sumLoading ? (
                  panelSpinner
                ) : sumData ? (
                  <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-gray-500">Status</dt>
                    <dd className="text-gray-700">
                      {getStatusPill(sumData.server_status)}
                    </dd>
                    <dt className="text-gray-500">Version</dt>
                    <dd className="tabular-nums text-gray-700">
                      {sumData.server_version || "—"}
                    </dd>
                    <dt className="text-gray-500">Uptime</dt>
                    <dd className="tabular-nums text-gray-700">
                      {sumData.server_uptime?.formatted || "—"}
                    </dd>
                    <dt className="text-gray-500">Last updated</dt>
                    <dd className="tabular-nums text-gray-700">
                      {formatTimestamp(sumData.timestamp)}
                    </dd>
                  </dl>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="UMD server">
                {data ? (
                  <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-gray-500">Status</dt>
                    <dd className="text-gray-700">
                      {getStatusPill(data.server_status)}
                    </dd>
                    <dt className="text-gray-500">Version</dt>
                    <dd className="tabular-nums text-gray-700">
                      {data.server_version}
                    </dd>
                    <dt className="text-gray-500">Uptime</dt>
                    <dd className="tabular-nums text-gray-700">
                      {data.server_uptime.formatted}
                    </dd>
                    <dt className="text-gray-500">Last updated</dt>
                    <dd className="tabular-nums text-gray-700">
                      {formatTimestamp(data.timestamp)}
                    </dd>
                  </dl>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="Paymod server">
                {paymodLoading ? (
                  panelSpinner
                ) : paymodError ? (
                  <p className="text-sm text-red-600">{paymodError}</p>
                ) : paymodData ? (
                  <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-gray-500">Status</dt>
                    <dd className="text-gray-700">{getStatusPill("ok")}</dd>
                    <dt className="text-gray-500">Version</dt>
                    <dd className="tabular-nums text-gray-700">
                      {paymodData.version}
                    </dd>
                    <dt className="text-gray-500">Uptime</dt>
                    <dd className="tabular-nums text-gray-700">
                      {paymodData.jvm.uptime}
                    </dd>
                    <dt className="text-gray-500">Last updated</dt>
                    <dd className="tabular-nums text-gray-700">
                      {formatTimestamp(paymodData.timestamp)}
                    </dd>
                  </dl>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="AI engine server">
                {aiEngineLoading ? (
                  panelSpinner
                ) : aiEngineError ? (
                  <p className="text-sm text-red-600">{aiEngineError}</p>
                ) : aiEngineData ? (
                  <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-gray-500">Status</dt>
                    <dd className="text-gray-700">
                      {aiEngineData.status === "degraded" ? (
                        <HealthPill tone="warn">Degraded</HealthPill>
                      ) : (
                        getStatusPill(aiEngineData.status)
                      )}
                    </dd>
                    <dt className="text-gray-500">Version</dt>
                    <dd className="tabular-nums text-gray-700">
                      {aiEngineData.version || "—"}
                    </dd>
                    <dt className="text-gray-500">Uptime</dt>
                    <dd className="tabular-nums text-gray-700">
                      {aiEngineData.uptime?.formatted || "—"}
                    </dd>
                    <dt className="text-gray-500">Last updated</dt>
                    <dd className="tabular-nums text-gray-700">
                      {formatTimestamp(aiEngineData.timestamp)}
                    </dd>
                  </dl>
                ) : (
                  noData
                )}
              </ServicePanel>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              Memory usage
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <ServicePanel title="SUM server">
                {sumLoading ? (
                  panelSpinner
                ) : sumData?.memory ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Heap used"
                      value={`${sumData.memory.heap_used_mb.toFixed(1)} MB`}
                      valueClassName={
                        sumData.memory.heap_used_vs_limit_percentage != null
                          ? getUsageColor(
                              sumData.memory.heap_used_vs_limit_percentage
                            )
                          : undefined
                      }
                      hint={
                        sumData.memory.heap_used_vs_limit_percentage != null
                          ? `${sumData.memory.heap_used_vs_limit_percentage.toFixed(
                              1
                            )}%`
                          : "N/A"
                      }
                    />
                    <Metric
                      label="Heap max"
                      value={
                        sumData.memory.heap_size_limit_mb != null
                          ? `${sumData.memory.heap_size_limit_mb.toFixed(1)} MB`
                          : "N/A"
                      }
                    />
                    <Metric
                      label="RSS memory"
                      value={`${sumData.memory.rss_mb.toFixed(1)} MB`}
                    />
                    <Metric
                      label="External"
                      value={`${sumData.memory.external_mb.toFixed(1)} MB`}
                    />
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="UMD server">
                {data ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Heap used"
                      value={`${data.memory.heap_used_mb.toFixed(1)} MB`}
                      valueClassName={
                        data.memory.memory_used_vs_container_limit_percentage !=
                        null
                          ? getUsageColor(
                              data.memory
                                .memory_used_vs_container_limit_percentage
                            )
                          : undefined
                      }
                      hint={
                        data.memory.memory_used_vs_container_limit_percentage !=
                        null
                          ? `${data.memory.memory_used_vs_container_limit_percentage.toFixed(
                              1
                            )}%`
                          : "N/A"
                      }
                    />
                    <Metric
                      label="Heap max"
                      value={
                        data.memory.heap_size_limit_mb != null
                          ? `${data.memory.heap_size_limit_mb.toFixed(1)} MB`
                          : "N/A"
                      }
                    />
                    <Metric
                      label="RSS memory"
                      value={`${data.memory.rss_mb.toFixed(1)} MB`}
                    />
                    <Metric
                      label="External"
                      value={`${data.memory.external_mb.toFixed(1)} MB`}
                    />
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="Paymod server">
                {paymodLoading ? (
                  panelSpinner
                ) : paymodData ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Heap used"
                      value={paymodData.memory.heap.used}
                      valueClassName={getUsageColor(
                        paymodData.memory.heap.usagePercent
                      )}
                      hint={`${paymodData.memory.heap.usagePercent.toFixed(
                        1
                      )}%`}
                    />
                    <Metric
                      label="Heap max"
                      value={paymodData.memory.heap.max}
                    />
                    <Metric
                      label="Non-heap"
                      value={paymodData.memory.nonHeap.used}
                    />
                    <Metric
                      label="Runtime free"
                      value={paymodData.memory.runtime.free}
                    />
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="AI engine server">
                {aiEngineLoading ? (
                  panelSpinner
                ) : aiEngineData?.memory ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Heap used"
                      value={`${aiEngineData.memory.heap_used_mb.toFixed(
                        1
                      )} MB`}
                      valueClassName={
                        aiEngineData.memory.heap_used_vs_limit_percentage !=
                        null
                          ? getUsageColor(
                              aiEngineData.memory.heap_used_vs_limit_percentage
                            )
                          : undefined
                      }
                      hint={
                        aiEngineData.memory.heap_used_vs_limit_percentage !=
                        null
                          ? `${aiEngineData.memory.heap_used_vs_limit_percentage.toFixed(
                              1
                            )}%`
                          : "N/A"
                      }
                    />
                    <Metric
                      label="Heap max"
                      value={
                        aiEngineData.memory.heap_size_limit_mb != null
                          ? `${aiEngineData.memory.heap_size_limit_mb.toFixed(
                              1
                            )} MB`
                          : "N/A"
                      }
                    />
                    <Metric
                      label="RSS memory"
                      value={`${aiEngineData.memory.rss_mb.toFixed(1)} MB`}
                    />
                    <Metric
                      label="External"
                      value={`${aiEngineData.memory.external_mb.toFixed(1)} MB`}
                    />
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              CPU information
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <ServicePanel title="SUM server">
                {sumLoading ? (
                  panelSpinner
                ) : sumData?.cpu ? (
                  <div className="space-y-3">
                    <Metric
                      label="CPU model"
                      value={
                        <span className="text-sm">{sumData.cpu.model}</span>
                      }
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Metric label="Cores" value={sumData.cpu.cores} />
                      <Metric
                        label="Usage"
                        value={`${sumData.cpu.usage_percentage.toFixed(1)}%`}
                        valueClassName={getUsageColor(
                          sumData.cpu.usage_percentage
                        )}
                      />
                    </div>
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="UMD server">
                {data ? (
                  <div className="space-y-3">
                    <Metric
                      label="CPU model"
                      value={<span className="text-sm">{data.cpu.model}</span>}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Metric label="Cores" value={data.cpu.cores} />
                      <Metric
                        label="Usage"
                        value={`${data.cpu.usage_percentage.toFixed(1)}%`}
                        valueClassName={getUsageColor(
                          data.cpu.usage_percentage
                        )}
                      />
                    </div>
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="Paymod server">
                {paymodLoading ? (
                  panelSpinner
                ) : paymodData ? (
                  <div className="space-y-3">
                    <Metric
                      label="Architecture"
                      value={
                        <span className="text-sm">{paymodData.cpu.model}</span>
                      }
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Metric label="Cores" value={paymodData.cpu.cores} />
                      {paymodData.cpu.cpuUsage && (
                        <Metric
                          label="CPU usage"
                          value={paymodData.cpu.cpuUsage}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="AI engine server">
                {aiEngineLoading ? (
                  panelSpinner
                ) : aiEngineData?.cpu ? (
                  <div className="space-y-3">
                    <Metric
                      label="CPU model"
                      value={
                        <span className="text-sm">
                          {aiEngineData.cpu.model}
                        </span>
                      }
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Metric label="Cores" value={aiEngineData.cpu.cores} />
                      <Metric
                        label="Usage"
                        value={`${aiEngineData.cpu.usage_percentage.toFixed(
                          1
                        )}%`}
                        valueClassName={getUsageColor(
                          aiEngineData.cpu.usage_percentage
                        )}
                      />
                    </div>
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              Database status
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <ServicePanel title="UMD server">
                {data ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Status"
                      value={getStatusPill(data.database.status)}
                    />
                    <Metric
                      label="Response time"
                      value={`${data.database.response_time_ms} ms`}
                    />
                    <Metric
                      label="Pool size"
                      value={data.database.connection_pool.pool_size}
                    />
                    <Metric
                      label="Pool min"
                      value={data.database.connection_pool.pool_min}
                    />
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="Paymod server">
                {paymodLoading ? (
                  panelSpinner
                ) : paymodData ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Status"
                      value={getStatusPill(paymodData.database.status)}
                    />
                    {paymodData.database.pingTimeMs !== undefined && (
                      <Metric
                        label="Ping time"
                        value={`${paymodData.database.pingTimeMs} ms`}
                      />
                    )}
                    {paymodData.database.poolSize !== undefined && (
                      <Metric
                        label="Pool size"
                        value={paymodData.database.poolSize}
                      />
                    )}
                    {paymodData.database.poolMin !== undefined && (
                      <Metric
                        label="Pool min"
                        value={paymodData.database.poolMin}
                      />
                    )}
                    {paymodData.database.activeConnections !== undefined && (
                      <Metric
                        label="Active connections"
                        value={paymodData.database.activeConnections}
                      />
                    )}
                    {paymodData.database.totalConnections !== undefined && (
                      <Metric
                        label="Total connections"
                        value={paymodData.database.totalConnections}
                      />
                    )}
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="AI engine server">
                {aiEngineLoading ? (
                  panelSpinner
                ) : aiEngineData?.database ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Status"
                      value={getStatusPill(aiEngineData.database.status)}
                    />
                    {aiEngineData.database.response_time_ms != null && (
                      <Metric
                        label="Response time"
                        value={`${aiEngineData.database.response_time_ms} ms`}
                      />
                    )}
                    {aiEngineData.database.connection_pool?.pool_size !=
                      null && (
                      <Metric
                        label="Pool size"
                        value={aiEngineData.database.connection_pool.pool_size}
                      />
                    )}
                    {aiEngineData.database.connection_pool?.pool_max !=
                      null && (
                      <Metric
                        label="Pool max"
                        value={aiEngineData.database.connection_pool.pool_max}
                      />
                    )}
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              Cache status
            </h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <ServicePanel title="SUM server">
                {sumLoading ? (
                  panelSpinner
                ) : sumData?.cache ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Node cache keys"
                      value={sumData.cache.node_cache_key_count ?? "—"}
                    />
                    <Metric
                      label="Hits / misses"
                      value={`${sumData.cache.hits ?? "—"} / ${
                        sumData.cache.misses ?? "—"
                      }`}
                    />
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="UMD server">
                {data ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Metric
                      label="Redis active"
                      value={
                        <HealthPill
                          tone={data.cache.redis_active ? "ok" : "down"}
                        >
                          {data.cache.redis_active ? "Yes" : "No"}
                        </HealthPill>
                      }
                    />
                    <Metric
                      label="Redis status"
                      value={getStatusPill(data.cache.redis_status)}
                    />
                    <Metric
                      label="Redis response"
                      value={`${data.cache.redis_response_time_ms} ms`}
                    />
                    <Metric
                      label="Redis keys"
                      value={data.cache.redis_key_count}
                    />
                    <Metric
                      label="Node cache keys"
                      value={data.cache.node_cache_key_count ?? "—"}
                      hint={
                        data.cache.node_cache_hits === undefined
                          ? undefined
                          : `${data.cache.node_cache_hits} hits / ${
                              data.cache.node_cache_misses ?? 0
                            } misses`
                      }
                    />
                    <Metric
                      label="Meta keys cache"
                      value={
                        data.cache.node_cache_details === undefined ? (
                          "—"
                        ) : (
                          <HealthPill
                            tone={
                              data.cache.node_cache_details.meta_key_cache
                                ? "ok"
                                : "warn"
                            }
                          >
                            {data.cache.node_cache_details.meta_key_cache
                              ? "Warm"
                              : "Cold"}
                          </HealthPill>
                        )
                      }
                      hint={
                        data.cache.node_cache_details === undefined
                          ? undefined
                          : `${data.cache.node_cache_details.event_dedup_entries} event dedup entries`
                      }
                    />
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>

              <ServicePanel title="Paymod server">
                {paymodLoading ? (
                  panelSpinner
                ) : paymodData ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Metric
                        label="Total keys"
                        value={paymodData.cache.totalKeys}
                      />
                      <Metric
                        label="Cache count"
                        value={paymodData.cache.cacheCount}
                      />
                    </div>

                    {paymodData.cache.cacheCount > 0 && (
                      <div className="rounded-lg border border-gray-200 bg-white p-3">
                        <p className="mb-2 text-xs text-gray-500">
                          Cache details
                        </p>
                        <div className="max-h-48 space-y-2 overflow-y-auto">
                          {Object.entries(paymodData.cache.caches).map(
                            ([cacheName, cacheDetails]) => (
                              <div
                                key={cacheName}
                                className="rounded border border-gray-200 bg-gray-50 p-2"
                              >
                                <div className="mb-1 flex items-center justify-between gap-2">
                                  <span className="truncate text-sm font-medium text-gray-900">
                                    {cacheName}
                                  </span>
                                  <HealthPill tone="neutral">
                                    {cacheDetails.type}
                                  </HealthPill>
                                </div>
                                <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                                  <dt className="text-gray-500">Size</dt>
                                  <dd className="tabular-nums text-gray-700">
                                    {cacheDetails.size}
                                  </dd>
                                  {cacheDetails.hitRate && (
                                    <>
                                      <dt className="text-gray-500">
                                        Hit rate
                                      </dt>
                                      <dd className="tabular-nums text-gray-700">
                                        {cacheDetails.hitRate}
                                      </dd>
                                    </>
                                  )}
                                  {cacheDetails.hitCount !== undefined && (
                                    <>
                                      <dt className="text-gray-500">Hits</dt>
                                      <dd className="tabular-nums text-gray-700">
                                        {cacheDetails.hitCount}
                                      </dd>
                                    </>
                                  )}
                                  {cacheDetails.missCount !== undefined && (
                                    <>
                                      <dt className="text-gray-500">Misses</dt>
                                      <dd className="tabular-nums text-gray-700">
                                        {cacheDetails.missCount}
                                      </dd>
                                    </>
                                  )}
                                  {cacheDetails.evictionCount !== undefined && (
                                    <>
                                      <dt className="text-gray-500">
                                        Evictions
                                      </dt>
                                      <dd className="tabular-nums text-gray-700">
                                        {cacheDetails.evictionCount}
                                      </dd>
                                    </>
                                  )}
                                </dl>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  noData
                )}
              </ServicePanel>
            </div>
          </section>

          {paymodData && (
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">
                Paymod thread information
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Metric
                  label="Thread count"
                  value={paymodData.jvm.threads.threadCount}
                />
                <Metric
                  label="Peak threads"
                  value={paymodData.jvm.threads.peakThreadCount}
                />
                <Metric
                  label="Daemon threads"
                  value={paymodData.jvm.threads.daemonThreadCount}
                />
              </div>
            </section>
          )}

          {data && (
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">
                UMD environment configuration
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Metric
                  label="Node environment"
                  value={
                    <HealthPill tone="neutral">
                      {data.environment.node_env}
                    </HealthPill>
                  }
                />
                <Metric
                  label="Logger level"
                  value={
                    <HealthPill tone="neutral">
                      {data.environment.logger_level}
                    </HealthPill>
                  }
                />
                <Metric
                  label="APM active"
                  value={
                    <HealthPill
                      tone={data.environment.apm_active ? "ok" : "down"}
                    >
                      {data.environment.apm_active ? "Yes" : "No"}
                    </HealthPill>
                  }
                />
                <Metric
                  label="Cron active"
                  value={
                    <HealthPill
                      tone={
                        data.environment.cron_active === "true" ? "ok" : "down"
                      }
                    >
                      {data.environment.cron_active === "true" ? "Yes" : "No"}
                    </HealthPill>
                  }
                />
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default System;
